import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';

import useAddMessage from '@/hooks/useAddMessage';
import useDismissMessage from '@/hooks/useDismissMessage';
import { ToastMessage } from '@/types/appMessage';
import Toast from '@/ui/components/Toast';

import '@/services/i18n';
import { MembersContextProvider } from './contexts/membersContext';
import { OnboardingContextProvider } from './contexts/onboardingContext';
import TrackerProvider from './contexts/trackerContext';
import { Me } from './pages/members/types';
import Router from './Router';
import determineEnvFromLocation from './utils/determineEnvFromLocation';
import determineServerUrlFromLocation from './utils/determineServerUrlFromLocation';
import formatPersonName from './utils/formatPersonName';
import isRunningInReactNativeWebView from './utils/isRunningInReactNativeWebView';

const initialMemberData: Me = {
  id: '',
  person: {
    email: '',
    exclude_from_analytics: false,
    first_name: '',
    id: '',
    last_name: '',
    nickname: '',
    phone: '',
    profile_image_url: '',
    pronouns: '',
    time_zone: ''
  }
};

const App = () => {
  const queryClient = new QueryClient();
  const host = determineServerUrlFromLocation();
  const env = determineEnvFromLocation();
  const [memberData, setMemberData] = useState<Me>(initialMemberData);
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const addMessage = useAddMessage(setMessages);
  const dismissMessage = useDismissMessage(setMessages);

  const membersContextValue = {
    addMessage,
    onboardingCompleted: false,
    host: host,
    env: env,
    memberData,
    setMemberData,
    isRunningInReactNativeWebView: isRunningInReactNativeWebView(),
    getMemberFullName: (withPronouns: boolean = true, withNickname: boolean = true) => {
      return formatPersonName({ person: memberData.person, withNickname, withPronouns });
    },
    getMemberPublicName: () => {
      return formatPersonName({
        person: memberData.person,
        withNickname: false,
        withPronouns: false,
        lastInitialOnly: true
      });
    }
  };

  return (
    <Sentry.ErrorBoundary showDialog>
      <TrackerProvider>
        <MembersContextProvider value={membersContextValue}>
          <QueryClientProvider client={queryClient}>
            <OnboardingContextProvider>
              <div>
                <Router />

                {!!messages.length &&
                  messages.map((message, idx) => (
                    <Toast
                      key={`toast-message-${idx}-${message.id}}`}
                      index={idx}
                      message={{
                        level: message.level,
                        text: message.text,
                        persistent: false
                      }}
                      dismissMessage={() => dismissMessage(message.id)}
                    />
                  ))}
              </div>
            </OnboardingContextProvider>
          </QueryClientProvider>
        </MembersContextProvider>
      </TrackerProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
